import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { MoreHorizontal, Edit, Trash2, Eye, Truck, CheckCircle, XCircle, ArrowUpDown } from 'lucide-react';
import { StockTransferOrderListItem, StockTransferStatus, getStatusColor, getStatusLabel } from '@/app/types/stockTransfer';
import { format } from 'date-fns';

// Custom events for actions
export const editStockTransferEvent = 'editStockTransfer';
export const deleteStockTransferEvent = 'deleteStockTransfer';
export const viewStockTransferEvent = 'viewStockTransfer';
export const updateStatusEvent = 'updateStockTransferStatus';

// Helper to dispatch custom events
const dispatchCustomEvent = (eventName: string, detail: any) => {
  const event = new CustomEvent(eventName, { detail });
  document.dispatchEvent(event);
};

// Status badge component
const StatusBadge: React.FC<{ status: StockTransferStatus }> = ({ status }) => {
  const color = getStatusColor(status);
  const label = getStatusLabel(status);
  
  const variantMap: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    gray: 'secondary',
    blue: 'default',
    green: 'outline',
    red: 'destructive',
  };
  
  const iconMap: Record<StockTransferStatus, React.ReactNode> = {
    draft: null,
    in_transit: <Truck className="h-3 w-3 mr-1" />,
    completed: <CheckCircle className="h-3 w-3 mr-1" />,
    cancelled: <XCircle className="h-3 w-3 mr-1" />,
  };
  
  return (
    <Badge variant={variantMap[color] || 'default'} className="font-medium">
      <span className="flex items-center">
        {iconMap[status]}
        {label}
      </span>
    </Badge>
  );
};

// Column definitions
export const columns: ColumnDef<StockTransferOrderListItem>[] = [
  {
    accessorKey: 'document_number',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-3"
        >
          Document No.
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const documentNumber = row.getValue('document_number') as string;
      return (
        <div className="font-medium">{documentNumber}</div>
      );
    },
  },
  {
    accessorKey: 'transaction_date',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-3"
        >
          Date
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const date = row.getValue('transaction_date') as string;
      return format(new Date(date), 'dd/MM/yyyy');
    },
  },
  {
    id: 'transfer_route',
    header: 'From → To',
    cell: ({ row }) => {
      const transfer = row.original;
      const from = transfer.source_godown?.name || `Godown ${transfer.source_godown_id}`;
      const to = transfer.destination_godown?.name || `Godown ${transfer.destination_godown_id}`;
      
      return (
        <div className="flex items-center gap-2">
          <span className="text-sm">{from}</span>
          <span className="text-muted-foreground">→</span>
          <span className="text-sm">{to}</span>
        </div>
      );
    },
  },
  {
    accessorKey: 'status',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-3"
        >
          Status
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const status = row.getValue('status') as StockTransferStatus;
      return <StatusBadge status={status} />;
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },
  {
    accessorKey: 'reference_number',
    header: 'Reference',
    cell: ({ row }) => {
      const reference = row.getValue('reference_number') as string | null;
      return reference ? (
        <span className="text-sm text-muted-foreground">{reference}</span>
      ) : (
        <span className="text-sm text-muted-foreground">-</span>
      );
    },
  },
  {
    accessorKey: 'items_count',
    header: 'Items',
    cell: ({ row }) => {
      const count = row.original.items_count || 0;
      return (
        <div className="text-center">
          <Badge variant="secondary">{count}</Badge>
        </div>
      );
    },
  },
  {
    accessorKey: 'total_value',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-3"
        >
          Value
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const value = parseFloat(row.getValue('total_value') as string || '0');
      return (
        <div className="text-right font-medium">
          ₹{value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      );
    },
  },
  {
    id: 'actions',
    cell: ({ row }) => {
      const transfer = row.original;
      const canEdit = transfer.status === 'draft';
      const canDelete = transfer.status === 'draft';
      
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => dispatchCustomEvent(viewStockTransferEvent, { transfer })}
              className="cursor-pointer"
            >
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </DropdownMenuItem>
            {canEdit && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => dispatchCustomEvent(editStockTransferEvent, { transfer })}
                  className="cursor-pointer"
                >
                  <Edit className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
              </>
            )}
            {canDelete && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => dispatchCustomEvent(deleteStockTransferEvent, { transfer })}
                  className="cursor-pointer text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];

// Filter options for status
export const statusFilterOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];